import React from "react";
import { IconButton } from "@mui/material";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { formatDate } from "utils/dateFormats";
import HighlightedText from "components/Shared/HighlightedText";
import WardForm from "./WardForm";
import FormDrawer from "components/Shared/FormDrawer";
import { useUpdateMunicipalityMutation } from "store/services/LocationsApi/municipalityApi";
import { toast } from "react-toastify";

const WardList = ({
  municipalityWards,
  municipalityDetail,
  isBusy = false,
  totalWards,
  perPage,
  currentPage,
  handlePageChange,
  handlePerRowsChange,
}) => {
  const [showWardForm, setShowWardForm] = React.useState(false);
  const [selectedWard, setSelectedWard] = React.useState(null);

  const handleCancelWard = () => {
    setShowWardForm(false);
    setSelectedWard(null);
  };

  const [updateWard, { isLoading: updatingWard }] =
    useUpdateMunicipalityMutation();

  const handleUpdateWard = async (data) => {
    try {
      await updateWard({
        ...data,
        id: municipalityDetail?._id,
      }).unwrap();
      toast.success("Ward updated successfully");
      handleCancelWard();
    } catch (err) {
      toast.error(
        err?.data?.message ?? err?.message ?? "Couldn't update Ward!"
      );
    }
  };

  const columns = [
    {
      name: "Ward Name",
      selector: (row) => row?.name,
      cell: (row) => (row?.name ? <HighlightedText text={row?.name} /> : "N/A"),
      sortable: true,
    },
    {
      name: "Ward Code",
      selector: (row) => row?.code,
      sortable: true,
    },
    {
      name: "Created At",
      selector: (row) => row?.createdAt,
      cell: (row) => (
        <div title={row?.createdAt} className="line-clamp-2">
          {formatDate(row?.createdAt)}
        </div>
      ),
      minWidth: "140px",
    },
    {
      name: "Updated At",
      selector: (row) => row?.updatedAt,
      cell: (row) => (
        <div title={row?.updatedAt} className="line-clamp-2">
          {formatDate(row?.updatedAt)}
        </div>
      ),
      minWidth: "140px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <IconButton
            onClick={() => {
              setSelectedWard(row);
              setShowWardForm(true);
            }}
            size="small"
            aria-label="Edit Ward"
          >
            <ModeEditOutlineTwoToneIcon />
          </IconButton>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  return (
    <BreadcrumbLayout.Paper>
      <AppTable
        columns={columns}
        data={municipalityWards}
        paginationServer
        paginationPerPage={perPage}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        progressPending={isBusy}
        paginationTotalRows={totalWards}
      />

      <FormDrawer
        title="Edit Ward"
        open={showWardForm}
        onClose={handleCancelWard}
      >
        <WardForm
          wardDetail={selectedWard}
          municipalityDetail={municipalityDetail}
          isBusy={updatingWard}
          onSubmit={(data) => handleUpdateWard(data)}
          onCancel={handleCancelWard}
        />
      </FormDrawer>
    </BreadcrumbLayout.Paper>
  );
};

export default WardList;
